/**
 * Template List Component
 * 
 * Displays all inspection templates for the shop with:
 * - Global templates (read-only)
 * - Shop-specific templates (editable)
 * - Draft/published status badges
 * - Actions: Edit, Clone, Create New
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInspectionTemplateEditor } from '@/hooks/useInspectionTemplateEditor';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { CreateTemplateDialog } from './CreateTemplateDialog';
import { Plus, Edit, Copy, Globe, Building2, FileText, CheckCircle2, Archive } from 'lucide-react';

interface TemplateListProps {
  shopId: string;
}

export function TemplateList({ shopId }: TemplateListProps) {
  const navigate = useNavigate();
  const editor = useInspectionTemplateEditor(shopId);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [cloneFromKey, setCloneFromKey] = useState<string | null>(null);

  if (editor.isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {[1, 2, 3].map((i) => (
          <Card key={i}>
            <CardHeader>
              <Skeleton className="h-5 w-32" />
              <Skeleton className="h-4 w-48" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-8 w-full" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  // Group templates by template_key, showing only the most relevant version
  const templatesByKey = editor.templates.reduce((acc, template) => {
    const existing = acc.get(template.template_key);
    
    // Prefer: shop-specific draft > shop-specific active > global active
    if (!existing) {
      acc.set(template.template_key, template);
    } else {
      const isShopSpecific = template.shop_id === shopId;
      const existingIsShopSpecific = existing.shop_id === shopId;
      
      if (isShopSpecific && !existingIsShopSpecific) {
        acc.set(template.template_key, template);
      } else if (isShopSpecific && existingIsShopSpecific) {
        // Prefer draft over published for editing
        if (template.status === 'draft' && existing.status !== 'draft') {
          acc.set(template.template_key, template);
        } else if (template.is_active && !existing.is_active && template.status !== 'draft') {
          acc.set(template.template_key, template);
        }
      }
    }
    
    return acc;
  }, new Map<string, typeof editor.templates[0]>());

  const groupedTemplates = Array.from(templatesByKey.values());

  const handleEdit = (template: typeof editor.templates[0]) => {
    if (template.status === 'draft') {
      navigate(`/inspection-templates/${template.id}`);
    } else {
      // Need to create a draft first
      setCloneFromKey(template.template_key);
      setCreateDialogOpen(true);
    }
  };

  const handleClone = (templateKey: string) => {
    setCloneFromKey(templateKey);
    setCreateDialogOpen(true);
  };

  const handleCreateNew = () => {
    setCloneFromKey(null);
    setCreateDialogOpen(true);
  };

  const handleCreateSuccess = (templateId: string) => {
    setCreateDialogOpen(false);
    setCloneFromKey(null);
    navigate(`/inspection-templates/${templateId}`);
  };

  const getStatusBadge = (template: typeof editor.templates[0]) => {
    if (template.status === 'draft') {
      return <Badge variant="outline" className="text-amber-600 border-amber-600">Draft</Badge>;
    }
    if (template.status === 'archived') {
      return <Badge variant="outline" className="text-muted-foreground"><Archive className="h-3 w-3 mr-1" /> Archived</Badge>;
    }
    if (template.is_active) {
      return <Badge variant="default" className="bg-green-600"><CheckCircle2 className="h-3 w-3 mr-1" /> Active</Badge>;
    }
    return <Badge variant="secondary">v{template.version}</Badge>;
  };

  return (
    <>
      <div className="flex justify-end mb-4">
        <Button onClick={handleCreateNew}>
          <Plus className="h-4 w-4 mr-2" />
          New Template
        </Button>
      </div>

      {groupedTemplates.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <FileText className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No templates yet</h3>
            <p className="text-muted-foreground text-center mb-4">
              Create your first inspection template to get started
            </p>
            <Button onClick={handleCreateNew}>
              <Plus className="h-4 w-4 mr-2" />
              Create Template
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {groupedTemplates.map((template) => {
            const isGlobal = template.is_global;
            const isShopSpecific = template.shop_id === shopId;
            
            return (
              <Card key={template.id} className="flex flex-col">
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      {isGlobal ? (
                        <Globe className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <Building2 className="h-4 w-4 text-primary" />
                      )}
                      <CardTitle className="text-base">
                        <button
                          type="button"
                          className="hover:underline text-left cursor-pointer"
                          onClick={() => navigate(`/inspection-templates/${template.id}`)}
                        >
                          {template.name}
                        </button>
                      </CardTitle>
                    </div>
                    {getStatusBadge(template)}
                  </div>
                  <CardDescription className="line-clamp-2">
                    {template.description || 'No description'}
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex-1 flex flex-col justify-end">
                  <div className="text-xs text-muted-foreground mb-3">
                    {isGlobal ? 'Global template' : `Version ${template.version}`}
                    {template.published_at && (
                      <> · Published {new Date(template.published_at).toLocaleDateString()}</>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {isGlobal ? (
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={() => handleClone(template.template_key)}
                      >
                        <Copy className="h-3 w-3 mr-1" />
                        Customize
                      </Button>
                    ) : (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          onClick={() => handleEdit(template)}
                        >
                          <Edit className="h-3 w-3 mr-1" />
                          {template.status === 'draft' ? 'Edit' : 'Edit (New Draft)'}
                        </Button>
                        {template.status !== 'draft' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/inspection-templates/${template.id}`)}
                          >
                            View
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <CreateTemplateDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        shopId={shopId}
        cloneFromKey={cloneFromKey}
        onSuccess={handleCreateSuccess}
      />
    </>
  );
}
